// ROI Evaluation Model for AI Projects

export interface ROICostStructure {
  subscriptions: number;       // 1.1 Suscripciones y Licenciamiento
  tokenConsumption: number;    // 1.2 Consumo de Tokens
  personnel: number;           // 1.3 Capital Humano
  training: number;            // 1.4 Costo de Capacitación
}

export type BenefitType =
  | 'external_hiring'     // Ahorro de Contratación Externa
  | 'time_savings'        // Ahorro de Tiempo Anual
  | 'error_reduction'     // Reducción de Errores
  | 'new_revenue'         // Nuevos Ingresos
  | 'scalability'         // Escalabilidad
  | 'other'               // Otros
  | 'not_defined';        // No definido (intangible, $0)

export interface ROIBenefitItem {
  type: BenefitType;
  amount: number;
  description: string;
}

export interface ProjectROIData {
  costs: ROICostStructure;
  benefits: ROIBenefitItem[];
}

export const BENEFIT_TYPE_LABELS: Record<BenefitType, string> = {
  external_hiring: 'Ahorro de Contratación Externa',
  time_savings: 'Ahorro de Tiempo Anual',
  error_reduction: 'Reducción de Errores',
  new_revenue: 'Nuevos Ingresos',
  scalability: 'Escalabilidad',
  other: 'Otros',
  not_defined: 'No definido (intangible)',
};

export const BENEFIT_TYPE_DESCRIPTIONS: Record<BenefitType, string> = {
  external_hiring: 'Valor total del contrato anual evitado por no pagar a terceros o agencias.',
  time_savings: 'Horas ahorradas × Valor hora del empleado.',
  error_reduction: 'Costo promedio del error × Frecuencia anual.',
  new_revenue: 'Margen neto de las nuevas ventas generadas gracias a la IA.',
  scalability: 'Salario anual total que se dejó de contratar ante el crecimiento.',
  other: 'Beneficios específicos no categorizados con valor estimado documentado.',
  not_defined: 'Beneficios intangibles (Nivel 1-4). Valor de $0 para el cálculo del ROI financiero.',
};

export const COST_LABELS: Record<keyof ROICostStructure, { name: string; description: string }> = {
  subscriptions: {
    name: 'Suscripciones y Licenciamiento',
    description: 'Pago total anual por el uso de plataformas, SaaS o licencias de herramientas de IA.',
  },
  tokenConsumption: {
    name: 'Consumo de Tokens (Cómputo)',
    description: 'Costo variable basado en el volumen de uso de modelos de lenguaje (LLMs).',
  },
  personnel: {
    name: 'Capital Humano (Personal)',
    description: 'Valor del tiempo dedicado por el equipo interno: diseño de prompts, integración y supervisión.',
  },
  training: {
    name: 'Costo de Capacitación',
    description: 'Inversión en formación, sesiones en vivo y gestión del cambio para la adopción.',
  },
};

// --- Calculation Helpers ---

export function calculateTotalCosts(costs: ROICostStructure): number {
  return costs.subscriptions + costs.tokenConsumption + costs.personnel + costs.training;
}

export function calculateTotalBenefits(benefits: ROIBenefitItem[]): number {
  return benefits.reduce((sum, b) => sum + (b.type === 'not_defined' ? 0 : b.amount), 0);
}

export function calculateROI(totalBenefits: number, totalCosts: number): number {
  if (totalCosts === 0) return 0;
  return ((totalBenefits - totalCosts) / totalCosts) * 100;
}

export function calculateBCR(totalBenefits: number, totalCosts: number): number {
  if (totalCosts === 0) return 0;
  return totalBenefits / totalCosts;
}

export function calculatePaybackMonths(totalBenefits: number, totalCosts: number): number {
  if (totalBenefits === 0) return Infinity;
  return totalCosts / (totalBenefits / 12);
}

// --- Mock ROI Data for Portfolio Projects ---

export interface ProjectROISummary {
  projectId: string;
  projectName: string;
  company: string;
  costs: ROICostStructure;
  benefits: ROIBenefitItem[];
  totalCosts: number;
  totalBenefits: number;
  roi: number;
  bcr: number;
  paybackMonths: number;
}

export const mockProjectROIs: ProjectROISummary[] = [
  {
    projectId: '1',
    projectName: 'Chatbot de Atención al Cliente',
    company: 'TechCorp',
    costs: { subscriptions: 24000, tokenConsumption: 18000, personnel: 72000, training: 15000 },
    benefits: [
      { type: 'external_hiring', amount: 180000, description: 'Eliminación de contrato con call center externo' },
      { type: 'time_savings', amount: 220000, description: '4,400 horas ahorradas a $50/hora' },
      { type: 'error_reduction', amount: 45000, description: 'Reducción de tickets mal clasificados' },
    ],
    totalCosts: 129000,
    totalBenefits: 445000,
    roi: 245,
    bcr: 3.45,
    paybackMonths: 3.5,
  },
  {
    projectId: '2',
    projectName: 'Predicción de Demanda',
    company: 'RetailMax',
    costs: { subscriptions: 36000, tokenConsumption: 12000, personnel: 120000, training: 20000 },
    benefits: [
      { type: 'time_savings', amount: 150000, description: '3,000 horas de planificación manual' },
      { type: 'error_reduction', amount: 350000, description: 'Reducción de sobre-stock y faltantes' },
      { type: 'new_revenue', amount: 280000, description: 'Mejora en disponibilidad de producto' },
    ],
    totalCosts: 188000,
    totalBenefits: 780000,
    roi: 315,
    bcr: 4.15,
    paybackMonths: 2.9,
  },
  {
    projectId: '5',
    projectName: 'Detección de Fraude',
    company: 'FinanceBank',
    costs: { subscriptions: 60000, tokenConsumption: 45000, personnel: 200000, training: 35000 },
    benefits: [
      { type: 'error_reduction', amount: 1200000, description: 'Fraudes detectados y evitados' },
      { type: 'external_hiring', amount: 350000, description: 'Eliminación de auditoría externa' },
      { type: 'scalability', amount: 280000, description: 'Evita 4 contrataciones de analistas' },
    ],
    totalCosts: 340000,
    totalBenefits: 1830000,
    roi: 438,
    bcr: 5.38,
    paybackMonths: 2.2,
  },
  {
    projectId: '6',
    projectName: 'Optimización de Rutas',
    company: 'LogiExpress',
    costs: { subscriptions: 18000, tokenConsumption: 8000, personnel: 95000, training: 12000 },
    benefits: [
      { type: 'time_savings', amount: 180000, description: 'Reducción de tiempos de entrega' },
      { type: 'other', amount: 120000, description: 'Ahorro en combustible y mantenimiento' },
      { type: 'scalability', amount: 200000, description: 'Cobertura de nuevas zonas sin contratar' },
    ],
    totalCosts: 133000,
    totalBenefits: 500000,
    roi: 276,
    bcr: 3.76,
    paybackMonths: 3.2,
  },
  {
    projectId: '9',
    projectName: 'Clasificación de Tickets',
    company: 'TechCorp',
    costs: { subscriptions: 12000, tokenConsumption: 6000, personnel: 30000, training: 8000 },
    benefits: [
      { type: 'time_savings', amount: 95000, description: '1,900 horas de clasificación manual' },
      { type: 'error_reduction', amount: 55000, description: 'Tickets mal ruteo eliminados' },
      { type: 'not_defined', amount: 0, description: 'Mejora en satisfacción del cliente' },
    ],
    totalCosts: 56000,
    totalBenefits: 150000,
    roi: 168,
    bcr: 2.68,
    paybackMonths: 4.5,
  },
  {
    projectId: '14',
    projectName: 'Asistente de Onboarding',
    company: 'HRSolutions',
    costs: { subscriptions: 9600, tokenConsumption: 4800, personnel: 48000, training: 18000 },
    benefits: [
      { type: 'time_savings', amount: 120000, description: '2,400 horas de onboarding manual' },
      { type: 'external_hiring', amount: 85000, description: 'Consultora de RRHH no renovada' },
      { type: 'scalability', amount: 72000, description: 'Escala a 3 sedes sin personal adicional' },
    ],
    totalCosts: 80400,
    totalBenefits: 277000,
    roi: 244,
    bcr: 3.44,
    paybackMonths: 3.5,
  },
];

// Aggregated ROI data for Enterprise Dashboard
export const aggregatedROIData = {
  totalInvestment: mockProjectROIs.reduce((s, p) => s + p.totalCosts, 0),
  totalBenefits: mockProjectROIs.reduce((s, p) => s + p.totalBenefits, 0),
  get roiPercentage() {
    return Math.round(calculateROI(this.totalBenefits, this.totalInvestment));
  },
  get bcrRatio() {
    return calculateBCR(this.totalBenefits, this.totalInvestment).toFixed(2);
  },
  get paybackMonths() {
    return calculatePaybackMonths(this.totalBenefits, this.totalInvestment).toFixed(1);
  },
  costBreakdown: [
    { category: 'Suscripciones y Licencias', amount: mockProjectROIs.reduce((s, p) => s + p.costs.subscriptions, 0), color: '#3b82f6' },
    { category: 'Consumo de Tokens', amount: mockProjectROIs.reduce((s, p) => s + p.costs.tokenConsumption, 0), color: '#8b5cf6' },
    { category: 'Capital Humano', amount: mockProjectROIs.reduce((s, p) => s + p.costs.personnel, 0), color: '#f59e0b' },
    { category: 'Capacitación', amount: mockProjectROIs.reduce((s, p) => s + p.costs.training, 0), color: '#10b981' },
  ],
  benefitBreakdown: (() => {
    const map: Record<string, number> = {};
    mockProjectROIs.forEach(p => {
      p.benefits.forEach(b => {
        if (b.type !== 'not_defined') {
          const label = BENEFIT_TYPE_LABELS[b.type];
          map[label] = (map[label] || 0) + b.amount;
        }
      });
    });
    const colors = ['#22c55e', '#3b82f6', '#f59e0b', '#8b5cf6', '#06b6d4', '#f97316'];
    return Object.entries(map).map(([category, amount], i) => ({
      category,
      amount,
      color: colors[i % colors.length],
    }));
  })(),
  monthlyROI: [
    { month: 'Ene', costos: 154400, beneficios: 66500 },
    { month: 'Feb', costos: 154400, beneficios: 166500 },
    { month: 'Mar', costos: 154400, beneficios: 333000 },
    { month: 'Abr', costos: 154400, beneficios: 555000 },
    { month: 'May', costos: 154400, beneficios: 832000 },
    { month: 'Jun', costos: 154400, beneficios: 1110000 },
    { month: 'Jul', costos: 154400, beneficios: 1450000 },
    { month: 'Ago', costos: 154400, beneficios: 1820000 },
    { month: 'Sep', costos: 154400, beneficios: 2250000 },
    { month: 'Oct', costos: 154400, beneficios: 2750000 },
    { month: 'Nov', costos: 154400, beneficios: 3300000 },
    { month: 'Dic', costos: 154400, beneficios: 3982000 },
  ],
};
